import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';

import { ButtonIcon } from '../button/interfaces';
import { ButtonMode, ButtonTheme } from '../button/enums';
import { ButtonLayout, ButtonPosition } from './enums';

/**
 * This component contains the cta button used during booking flows.
 *
 * @example
 * <b>Example with basic options</b>
 *
 * <code>
 *   <pre>
 *     <fcom-cta-button
 *       [position]="position"
 *       [visible]="visible"
 *       [title]="title"
 *       [disabled]="disabled"
 *       (buttonClicked)="click($event)">
 *       <span [innerHTML]="content"></span>
 *     </fcom-cta-button>
 *   </pre>
 * </code>
 */
@Component({
  selector: 'fcom-cta-button',
  templateUrl: './cta-button.component.html',
  styleUrls: ['./cta-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtaButtonComponent {
  private themeService = inject(FinnairThemeService);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonLayout = ButtonLayout;

  /** The text to display in the button */
  title = input<string>();
  secondaryButtonTitle = input<string>();
  showSecondaryButton = input(false);
  /** Whether the button should be visible or hidden (but still in the dom) */
  visible = input(true);
  /** Disables the buttons */
  disabled = input(false);
  secondaryButtonDisabled = input(false);
  removeContentPadding = input(false);
  isTabletUp = input(false);
  loading = input(false);
  icon = input<ButtonIcon>();
  secondaryButtonIcon = input<ButtonIcon>();
  theme = input<ButtonTheme>(ButtonTheme.LIGHT_PRIMARY_CTA_HEATHER);
  /** Controls the positioning of the button */
  position = input<ButtonPosition>(ButtonPosition.GRID);
  layout = input<ButtonLayout>(ButtonLayout.ROW);

  buttonClicked = output<Event>();
  secondaryButtonClicked = output<Event>();

  computedTheme = computed((): ButtonTheme => {
    const siteTheme: FinnairTheme = this.themeService.siteTheme();

    if (siteTheme === FinnairTheme.LEGACY) {
      return this.theme();
    }

    switch (this.theme()) {
      case ButtonTheme.LIGHT_PRIMARY_CTA_HEATHER:
      case ButtonTheme.DARK_PRIMARY_CTA_HEATHER:
      case ButtonTheme.PRIMARY_ACCENT:
        return ButtonTheme.PRIMARY_ACCENT;
      case ButtonTheme.DARK_SECONDARY_OUTLINE:
      case ButtonTheme.SECONDARY_OUTLINE_REVERSED:
        return ButtonTheme.SECONDARY_OUTLINE_REVERSED;
      case ButtonTheme.LIGHT_TERTIARY_GHOST:
      case ButtonTheme.TERTIARY_GHOST:
        return ButtonTheme.TERTIARY_GHOST;
      case ButtonTheme.DARK_TERTIARY_GHOST:
      case ButtonTheme.TERTIARY_GHOST_REVERSED:
        return ButtonTheme.TERTIARY_GHOST_REVERSED;
      default:
        return ButtonTheme.SECONDARY_OUTLINE;
    }
  });
}
